import React, { useState } from "react";
import "./Footer.css";

function Footer() {
  const [showPolicy, setShowPolicy] = useState(false);

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    setShowPolicy(true);
  };

  const closePolicy = () => {
    setShowPolicy(false);
  };

  return (
    <footer className="footer">
      {showPolicy && (
        <div className="privacy-modal-backdrop" role="dialog" aria-modal="true" aria-label="Privacy Policy">
          <div className="privacy-modal">
            <div className="privacy-modal-content">
              <h2>Privacy Policy</h2>
              <p><strong>Effective Date:</strong> December 14, 2025</p>
              <p><strong>Last Updated:</strong> December 14, 2025</p>

              <h3>1. Introduction</h3>
              <p>
                Welcome to GenieShop, a product of ConsumerGenie Corporation, a Delaware corporation ("we", "our", or "us").
              </p>
              <p>
                Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our website, applications, and AI-powered services (collectively, the "Services").
              </p>
              <p>
                This Policy applies globally and includes additional information for residents of certain regions (such as the European Economic Area, 
                United Kingdom, Canada, and U.S. states with privacy laws).
              </p>
              <p>
                By using our Services, you agree to the terms of this Privacy Policy. If you do not agree,
                please discontinue use of the Services.
              </p>

              <h3>2. Information We Collect</h3>
              <p>We may collect the following categories of information:</p>
              <ul>
                <li><strong>Personal Information:</strong> Your name and email address when you create an account, sign up for updates, or contact us.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our Services, including pages visited, time spent, features used and input/output data submitted through GenieShop.</li>
                <li><strong>Technical Data:</strong> We collect technical information such as your IP address, session ID, operating system, browser type (e.g., user agent string), and cookies. This information helps us ensure platform compatibility, maintain security, and analyze aggregate usage patterns. We collect this information automatically when you visit or interact with our website or services.</li>
                <li><strong>AI Interaction Data:</strong> Text or queries you provide to our AI systems and the generated outputs, which we may use in aggregated or de-identified form to improve accuracy and performance.</li>
                <li>Additional information you voluntarily provide through forms or communication channels.</li>
                <li><strong>Children's Data Protection:</strong> We do not knowingly collect personal data from individuals under the age of 16. If we become aware of such data, we delete it promptly.</li>
              </ul>

              <h3>3. How We Use Your Information</h3>
              <p>We use your information for purposes including:</p>
              <ul>
                <li>Providing, maintaining, and improving our Services.</li>
                <li>Processing and generating AI-based outputs in response to your input.</li>
                <li>Analyzing aggregated usage data to enhance performance and reliability.</li>
                <li>Communicating with you regarding updates or marketing (with consent).</li>
                <li>Ensuring legal compliance and protecting against misuse or fraud.</li>
                <li>Customer service and contract fulfillment.</li>
              </ul>

              <h3>4. Consent</h3>
              <p>We obtain your consent where required by law by:</p>
              <ul>
                <li>Account creation, agreeing to collection/use for service delivery.</li>
                <li>Explicit consent for marketing emails.</li>
                <li>Privacy policy acceptance through continued use.</li>
                <li>Cookies and similar technologies, with opt-out options provided where required.</li>
              </ul>
              <p>
                <strong>Note:</strong> Depending on your location, we may also rely on other lawful bases for processing such as performance of contract or legitimate interests (see regional sections below).
              </p>
              <p>
                You can withdraw consent anytime by updating preferences or contacting us.
              </p>

              <h3>5. How We Share Information</h3>
              <p>
                We do not sell your personal information. We may share limited data in the following situations:
              </p>
              <ul>
                <li><strong>Service Providers:</strong> With trusted vendors who help us operate and maintain our infrastructure (e.g., hosting, analytics, email delivery, customer support tools).</li>
                <li><strong>Legal Compliance:</strong> If required by law, regulation, or legal request, or to respond to valid government or regulatory inquiries.</li>
                <li><strong>Business Transactions:</strong> In the event of a merger, acquisition, financing, or asset sale involving ConsumerGenie Corporation or GenieShop.</li>
                <li><strong>Protection of Rights:</strong> When necessary to protect our rights, privacy, safety, or property, and/or that of our users or the public.</li>
              </ul>

              <h3>6. Cookies and Tracking Technologies</h3>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Remember user preferences and improve experience.</li>
                <li>Measure site performance and analytics.</li>
              </ul>
              <p>
                We use only essential cookies and privacy-friendly analytics. Non-essential cookies require your consent where required by law (e.g., in the EU/EEA and UK).
              </p>
              <p>
                You can manage or disable cookies through your browser settings or contact us to opt out of analytics.
              </p>

              <h3>7. Data Retention</h3>
              <p>
                We retain personal and usage data only as long as necessary for the purposes described in this Policy or as required by law.
              </p>
              <p>For example:</p>
              <ul>
                <li>Contact information (e.g., email addresses from waitlist signups) is kept until you unsubscribe or request deletion.</li>
                <li>Usage and technical data is retained for up to 12 months for analytics and security purposes, then aggregated or deleted.</li>
              </ul>

              <h3>8. International Data Transfers</h3>
              <p>
                ConsumerGenie Corporation is a U.S.-based company (Delaware corporation), and our Services may be accessed globally. Data may be processed and stored in the United States and other countries where our service providers operate.
              </p>
              <p>
                For users in regions with data transfer restrictions (such as the EU/EEA, UK, or Canada), we use appropriate safeguards permitted by law, such as standard contractual clauses, to protect your data during international transfers.
              </p>
              <p>
                By using our Services, you acknowledge these transfers in accordance with this Policy.
              </p>

              <h3>9. Sensitive Data Handling</h3>
              <p>
                We practice data minimization and do not collect or process sensitive personal information (such as health, racial, or religious data).
              </p>
              <p>
                Payment data, when processed, will be handled securely by third-party payment processors using encryption and industry standards (e.g., PCI-DSS). We do not store full payment details.
              </p>

              <h3>10. Data Security</h3>
              <p>
                We use appropriate administrative, technical, and physical safeguards to protect your data from unauthorized access, disclosure, or misuse. However, no system is completely secure, and we cannot guarantee absolute protection.
              </p>

              <h3>11. Your Rights</h3>
              <p>Depending on your jurisdiction, you may have rights to:</p>
              <ul>
                <li>Access or correct your personal information.</li>
                <li>Request deletion of your personal information.</li>
                <li>Withdraw consent where applicable.</li>
                <li>(Requests fulfilled within 30 days)</li>
              </ul>
              <p>To exercise these rights, contact us using the information below.</p>
              
              <h4>Additional Information for Residents of the European Economic Area (EEA) and United Kingdom</h4>
              <p>
                EEA/UK residents have rights under GDPR/UK GDPR including access, rectification, erasure ("right to be forgotten"), restriction, data portability, and objection to processing. We process your data based on contract performance, legitimate interests (e.g., analytics, security), or consent. You may also complain to your local data protection authority.
              </p>

              <h4>Additional Information for Residents of Canada</h4>
              <p>
                Canadian residents' data is handled in accordance with PIPEDA principles. You have rights to access, correction, and challenge our compliance.
              </p>

              <h4>Additional Information for Residents of the United States</h4>
              <p>
                U.S. residents in states with privacy laws (e.g., California CCPA/CPRA, Delaware DPDPA) may have additional rights including access, deletion, correction, and opt-out of sale/sharing/targeted advertising. We do not sell personal information but will honor valid requests where required.
              </p>

              <h4>Other Regions (including Middle East and Asia)</h4>
              <p>
                Residents of countries with data protection laws may have additional local rights. Contact us to exercise them, and we will comply where applicable law requires.
              </p>

              <h3>12. Privacy Complaints</h3>
              <p>
                Contact our Privacy Officer for complaints using the contact information below. We will investigate and respond promptly.
              </p>
              <p>Depending on your location, you may also escalate unresolved issues to your local data protection authority:</p>
              <ul>
                <li><strong>EEA/UK residents:</strong> Your national data protection authority (e.g., ICO in the UK).</li>
                <li><strong>Canadian residents:</strong> Office of the Privacy Commissioner of Canada.</li>
                <li><strong>U.S. residents:</strong> Relevant state attorney general or FTC where applicable.</li>
                <li><strong>Other regions:</strong> Your local data protection authority if one exists.</li>
              </ul>

              <h3>13. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy periodically as our Services evolve or legal requirements change. The revised version will be posted on our website with an updated "Last Updated" date and Effective Date.
              </p>
              <p>
                We will notify you of material changes by email (if we have your contact information) or through a prominent notice on our website.
              </p>

              <h3>14. Contact Us</h3>
              <p>If you have any questions or requests regarding this Privacy Policy, please contact us at:</p>
              <p>ConsumerGenie Corporation</p>
              <p>Email: info@consumergenie.net</p>
              <p>Registered office: 16192 Coastal Highway, Lewes, Delaware 19958, USA</p>
            </div>
            <button type="button" className="privacy-modal-close" onClick={closePolicy}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className="footer-content">
        <div className="footer-section">
          <h4>Contact Us</h4>
          <p>
            <a href="mailto:info@consumergenie.net">info@consumergenie.net</a>
          </p>
        </div>
        
        <div className="footer-section">
          <h4>Socials</h4>
          <div className="footer-socials">
            <div className="footer-social-row">
              <a className="btn-social" href="mailto:info@consumergenie.net" aria-label="Email">
                <span className="sr-only">Email</span>
                <span className="btn-social-icon">✉️</span>
              </a>
              <a className="btn-social" href="https://x.com/GenieConsumer" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <span className="sr-only">Twitter</span>
                <span className="btn-social-icon">𝕏</span>
              </a>
              <a className="btn-social" href="https://www.linkedin.com/company/genieshop-cg" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <span className="sr-only">LinkedIn</span>
                <span className="btn-social-icon">in</span>
              </a>
              <a className="btn-social" href="https://www.youtube.com/@ConsumerGenie" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <span className="sr-only">YouTube</span>
                <svg className="btn-social-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 8.5c0-1.5 1.2-2.5 2.6-2.5h10.8c1.4 0 2.6 1 2.6 2.5v7c0 1.5-1.2 2.5-2.6 2.5H6.6C5.2 18 4 17 4 15.5v-7z" stroke="currentColor" strokeWidth="1.6" fill="none" />
                  <path d="M10.5 10.2v3.6l3.2-1.8-3.2-1.8z" fill="currentColor" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        <div className="footer-section">
          <h4>Privacy Policy</h4>
          <p>
            <a href="#" onClick={handlePrivacyClick}>View Policy</a>
          </p>
        </div>
      </div>
      
      <div className="footer-copyright">
        <p>
          <a href="https://consumergenie.net/" target="_blank" rel="noopener noreferrer">
            ConsumerGenie Corporation
          </a>{" "}
          © 2026. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default Footer;

